import { useState } from 'react'
import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  ArrowLeft, Phone, Mail, User, Package, Calendar,
  MessageSquare, PhoneCall, FileText, ArrowRightLeft, Bell
} from 'lucide-react'

interface LeadFull {
  id: number
  name: string
  email: string | null
  phone: string | null
  pipeline: string
  pipeline_label: string
  status: string
  status_label: string
  source: string
  source_label: string
  temperature: string | null
  temperature_label: string | null
  reason: string | null
  notes: string | null
  message: string | null
  created_at: string
  converted_at: string | null
  lesson_package?: { id: number; name: string }
  student?: { id: number; name: string; email: string }
  student_subscription?: { id: number; status: string; lessons_remaining: number }
}

interface Interaction {
  id: number
  content: string
  interaction_type: string
  type_label: string
  user: { id: number; name: string }
  created_at: string
}

interface Props {
  lead: LeadFull
  interactions: Interaction[]
}

const INTERACTION_ICONS: Record<string, typeof MessageSquare> = {
  note: FileText,
  call: PhoneCall,
  message: MessageSquare,
  status_change: ArrowRightLeft
}


export default function CrmShow({ lead, interactions }: Props) {
  const [status, setStatus] = useState(lead.status)
  const [temperature, setTemperature] = useState(lead.temperature || '')
  const [notes, setNotes] = useState(lead.notes || '')
  const [notifTitle, setNotifTitle] = useState('')
  const [notifBody, setNotifBody] = useState('')
  const [sendingNotif, setSendingNotif] = useState(false)

  const isAcquisition = lead.pipeline === 'acquisition'

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus)
    router.patch(`/crm/${lead.id}`, { lead: { status: newStatus } }, { preserveState: true })
  }

  const handleTemperatureChange = (newTemp: string) => {
    setTemperature(newTemp)
    router.patch(`/crm/${lead.id}`, { lead: { temperature: newTemp } }, { preserveState: true })
  }

  const handleNotesBlur = () => {
    if (notes !== (lead.notes || '')) {
      router.patch(`/crm/${lead.id}`, { lead: { notes } }, { preserveState: true })
    }
  }

  const handleSendNotification = () => {
    if (!notifTitle.trim() || !notifBody.trim()) return
    setSendingNotif(true)
    router.post(`/crm/${lead.id}/notify`, {
      notification: { title: notifTitle, body: notifBody }
    }, {
      preserveState: false,
      onFinish: () => {
        setSendingNotif(false)
        setNotifTitle('')
        setNotifBody('')
      }
    })
  }

  const acquisitionStatuses = [
    { value: 'lesson_scheduled', label: 'Aula Experimental' },
    { value: 'lesson_done', label: 'Fez Aula / Sem Plano' },
    { value: 'converted', label: 'Assinou Plano' },
    { value: 'lost', label: 'Perdido' }
  ]

  const retentionStatuses = [
    { value: 'new_lead', label: 'Em Risco' },
    { value: 'contacted', label: 'Contatado' },
    { value: 'converted', label: 'Resolvido' },
    { value: 'lost', label: 'Perdido' }
  ]

  const statusOptions = isAcquisition ? acquisitionStatuses : retentionStatuses

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => router.visit(`/crm?pipeline=${lead.pipeline}`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{lead.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{lead.pipeline_label}</Badge>
              <Badge>{lead.status_label}</Badge>
              {lead.source_label && <Badge variant="secondary">{lead.source_label}</Badge>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Info & Controls */}
          <div className="lg:col-span-1 space-y-4">
            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">Informações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lead.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{lead.phone}</span>
                  </div>
                )}
                {lead.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{lead.email}</span>
                  </div>
                )}
                {lead.student && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <Button variant="link" className="p-0 h-auto text-sm" onClick={() => router.visit(`/users/${lead.student!.id}`)}>
                      {lead.student.name}
                    </Button>
                  </div>
                )}
                {lead.lesson_package && (
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span>{lead.lesson_package.name}</span>
                  </div>
                )}
                {lead.reason && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-red-500 font-medium">{lead.reason}</span>
                  </div>
                )}
                {lead.student_subscription && (
                  <div className="text-sm text-muted-foreground">
                    Assinatura: {lead.student_subscription.status} ({lead.student_subscription.lessons_remaining} aulas restantes)
                  </div>
                )}
                {lead.message && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Mensagem do formulário:</p>
                      <p className="text-sm">{lead.message}</p>
                    </div>
                  </>
                )}
                <div className="text-xs text-muted-foreground pt-2">
                  Criado em {lead.created_at}
                  {lead.converted_at && <> — Convertido em {lead.converted_at}</>}
                </div>
              </CardContent>
            </Card>

            {/* Status & Temperature Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">Gerenciar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Status</label>
                  <Select value={status} onValueChange={handleStatusChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {isAcquisition && (
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Temperatura</label>
                    <Select value={temperature} onValueChange={handleTemperatureChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hot">Quente</SelectItem>
                        <SelectItem value="warm">Morno</SelectItem>
                        <SelectItem value="cold">Frio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Observações</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={handleNotesBlur}
                    placeholder="Notas sobre este lead..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Timeline & Interactions */}
          <div className="lg:col-span-2 space-y-4">
            {/* Enviar Notificação Push */}
            {lead.student && (
              <Card className="border-blue-200 dark:border-blue-800">
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Enviar Notificação Push
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    value={notifTitle}
                    onChange={(e) => setNotifTitle(e.target.value)}
                    placeholder="Título da notificação"
                  />
                  <Textarea
                    value={notifBody}
                    onChange={(e) => setNotifBody(e.target.value)}
                    placeholder="Mensagem..."
                    rows={2}
                  />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSendNotification}
                      disabled={!notifTitle.trim() || !notifBody.trim() || sendingNotif}
                    >
                      <Bell className="h-4 w-4 mr-1" />
                      {sendingNotif ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Histórico ({interactions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {interactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Nenhuma interação registrada</p>
                ) : (
                  <div className="space-y-4">
                    {interactions.map((interaction) => {
                      const Icon = INTERACTION_ICONS[interaction.interaction_type] || FileText
                      const isStatusChange = interaction.interaction_type === 'status_change'

                      return (
                        <div key={interaction.id} className="flex gap-3">
                          <div className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full ${isStatusChange ? 'bg-purple-100 dark:bg-purple-900' : 'bg-muted'}`}>
                            <Icon className={`h-4 w-4 ${isStatusChange ? 'text-purple-600 dark:text-purple-400' : 'text-muted-foreground'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{interaction.user.name}</span>
                              <Badge variant="outline" className="text-xs">{interaction.type_label}</Badge>
                              <span className="text-xs text-muted-foreground ml-auto">{interaction.created_at}</span>
                            </div>
                            <p className={`text-sm mt-0.5 ${isStatusChange ? 'text-muted-foreground italic' : ''}`}>
                              {interaction.content}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
